/**
 * 🤖 SPACE AUTOMATION - Module Automatisation
 * Gestion du scheduler CRON et historique des emails
 * 
 * Features:
 * - Statut du scheduler (actif/inactif, prochaines exécutions)
 * - Historique des emails envoyés avec prévisualisation
 * - Logs d'exécution CRON
 * - Stats par type d'email et par jour
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock, Mail, CheckCircle, XCircle, RefreshCw, Eye,
  Calendar, BarChart3, Send, AlertTriangle, Play,
  Loader2, ChevronRight, X, Zap, Bot, Timer
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// 📧 Types d'emails avec labels et couleurs
const EMAIL_TYPES = {
  confirmation: { label: '📬 Confirmation', color: 'bg-blue-500' },
  welcome: { label: '🎉 Bienvenue', color: 'bg-green-500' },
  relance_j3: { label: '⏰ J+3', color: 'bg-yellow-500' },
  relance_j7: { label: '📢 J+7', color: 'bg-orange-500' },
  relance_j10: { label: '🔔 J+10', color: 'bg-red-400' },
  relance_j13: { label: '🚨 J+13', color: 'bg-red-600' }
};

// 📨 Modal de prévisualisation d'email
const EmailPreviewModal = ({ email, onClose }) => {
  if (!email) return null;

  const typeInfo = EMAIL_TYPES[email.type] || { label: email.type, color: 'bg-gray-500' };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-[#1a1a25] rounded-2xl border border-cyan-500/30 max-w-2xl w-full max-h-[80vh] overflow-x-hidden overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-cyan-500/20 flex items-center justify-between bg-gradient-to-r from-cyan-900/30 to-blue-900/30">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${typeInfo.color} flex items-center justify-center`}>
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-bold">{typeInfo.label}</p>
              <p className="text-xs text-gray-400">
                Envoyé le {new Date(email.sentAt).toLocaleDateString('fr-FR')} à {new Date(email.sentAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Métadonnées */}
        <div className="p-4 border-b border-cyan-500/20 bg-[#0f0f15]">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Destinataire:</span>
              <p className="text-white">{email.prenom} &lt;{email.email}&gt;</p>
            </div>
            <div>
              <span className="text-gray-500">Provider:</span>
              <p className="text-cyan-400 uppercase">{email.provider}</p>
            </div>
            <div className="col-span-2">
              <span className="text-gray-500">Objet:</span>
              <p className="text-white font-medium">{email.subject}</p>
            </div>
          </div>
        </div>

        {/* Corps du message */}
        <div className="p-4 overflow-y-auto max-h-[400px]">
          <div className="bg-white rounded-lg p-4 text-gray-800 font-mono text-sm whitespace-pre-wrap">
            {email.body}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-cyan-500/20 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-green-400">Envoyé avec succès</span>
          </div>
          <Button onClick={onClose} variant="outline" className="border-cyan-500/30">
            Fermer
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// 📊 Carte de statistique
const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
  <div className="bg-[#1a1a25] rounded-xl p-4 border border-cyan-500/20">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-sm text-gray-400">{label}</p>
        {subtext && <p className="text-xs text-cyan-400">{subtext}</p>}
      </div>
    </div>
  </div>
);

// 🤖 Composant principal
const SpaceAutomation = () => {
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [cronLogs, setCronLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [activeTab, setActiveTab] = useState('status'); // 'status', 'history', 'logs'
  const [isRunningCron, setIsRunningCron] = useState(false);

  // Charger les données
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Charger en parallèle
      const [statusRes, historyRes, logsRes] = await Promise.all([
        fetch(`${API}/api/cron/status`),
        fetch(`${API}/api/emails/history?limit=50`),
        fetch(`${API}/api/cron/logs?limit=20`)
      ]);

      if (statusRes.ok) {
        const data = await statusRes.json();
        setSchedulerStatus(data.scheduler);
      }

      if (historyRes.ok) {
        const data = await historyRes.json();
        setEmailHistory(data.emails || []);
        setEmailStats(data.stats || null);
      }

      if (logsRes.ok) {
        const data = await logsRes.json();
        setCronLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur de chargement des données');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Déclencher manuellement le CRON
  const triggerCron = async () => {
    setIsRunningCron(true);
    try {
      const response = await fetch(`${API}/api/cron/send-reminders`, { method: 'POST' });
      if (response.ok) {
        const result = await response.json();
        toast.success(`✅ ${result.total_sent || 0} emails envoyés !`);
        loadData(); // Rafraîchir les données
      } else {
        toast.error('Erreur lors de l\'exécution');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setIsRunningCron(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Automatisation</h1>
            <p className="text-gray-400 text-sm">Scheduler CRON & Historique Emails</p>
          </div>
        </div>
        <Button
          onClick={loadData}
          variant="outline"
          className="border-cyan-500/30 hover:bg-cyan-500/10"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 border-b border-cyan-500/20 pb-2">
        {[
          { id: 'status', label: 'Statut Scheduler', icon: Zap },
          { id: 'history', label: 'Historique Emails', icon: Mail },
          { id: 'logs', label: 'Logs CRON', icon: Clock }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu selon l'onglet */}
      {activeTab === 'status' && (
        <div className="space-y-6">
          {/* Statut du scheduler */}
          <div className="bg-[#1a1a25] rounded-xl p-6 border border-cyan-500/20">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Statut du Scheduler
              </h2>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                schedulerStatus?.running
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-red-500/20 text-red-400 border border-red-500/30'
              }`}>
                {schedulerStatus?.running ? '✅ Actif' : '❌ Inactif'}
              </div>
            </div>

            {/* Jobs planifiés */}
            <div className="space-y-3">
              {schedulerStatus?.jobs?.map((job, idx) => (
                <div
                  key={job.id}
                  className="flex items-center justify-between p-4 bg-[#0f0f15] rounded-lg border border-cyan-500/10"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      {job.name.includes('Email') ? (
                        <Mail className="w-5 h-5 text-purple-400" />
                      ) : (
                        <Timer className="w-5 h-5 text-purple-400" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">{job.name}</p>
                      <p className="text-xs text-gray-500">ID: {job.id}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-cyan-400 font-medium">{job.next_run_formatted}</p>
                    <p className="text-xs text-gray-500">Prochaine exécution</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Bouton exécution manuelle */}
            <div className="mt-6 pt-4 border-t border-cyan-500/20">
              <Button
                onClick={triggerCron}
                disabled={isRunningCron}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
              >
                {isRunningCron ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Exécution en cours...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Déclencher manuellement les relances
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 text-center mt-2">
                Exécute immédiatement l'envoi des relances J+3, J+7, J+10, J+13
              </p>
            </div>
          </div>

          {/* Stats rapides */}
          {emailStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={Mail}
                label="Emails envoyés"
                value={emailStats.total || 0}
                color="bg-cyan-500"
              />
              <StatCard
                icon={CheckCircle}
                label="Confirmations"
                value={emailStats.byType?.confirmation || 0}
                color="bg-blue-500"
              />
              <StatCard
                icon={Send}
                label="Relances"
                value={(emailStats.byType?.relance_j3 || 0) + (emailStats.byType?.relance_j7 || 0) + (emailStats.byType?.relance_j10 || 0) + (emailStats.byType?.relance_j13 || 0)}
                color="bg-orange-500"
              />
              <StatCard
                icon={Calendar}
                label="7 derniers jours"
                value={emailStats.last7Days?.reduce((sum, d) => sum + d.count, 0) || 0}
                color="bg-green-500"
              />
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-4">
          {/* Filtres par type */}
          <div className="flex gap-2 flex-wrap">
            {Object.entries(EMAIL_TYPES).map(([type, info]) => (
              <span
                key={type}
                className={`${info.color} text-white text-xs px-2 py-1 rounded-full`}
              >
                {info.label}: {emailStats?.byType?.[type] || 0}
              </span>
            ))}
          </div>

          {/* Liste des emails */}
          <div className="space-y-2">
            {emailHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Mail className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Aucun email dans l'historique</p>
              </div>
            ) : (
              emailHistory.map((email, idx) => {
                const typeInfo = EMAIL_TYPES[email.type] || { label: email.type, color: 'bg-gray-500' };
                return (
                  <motion.div
                    key={email.id || idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className="flex items-center gap-4 p-4 bg-[#1a1a25] rounded-xl border border-cyan-500/20 hover:border-cyan-500/40 transition-colors cursor-pointer"
                    onClick={() => setSelectedEmail(email)}
                  >
                    <div className={`w-10 h-10 rounded-lg ${typeInfo.color} flex items-center justify-center flex-shrink-0`}>
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium truncate">{email.prenom}</p>
                        <span className="text-xs text-gray-500 truncate">&lt;{email.email}&gt;</span>
                      </div>
                      <p className="text-sm text-gray-400 truncate">{email.subject}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-cyan-400">{typeInfo.label}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(email.sentAt).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <Eye className="w-4 h-4 text-gray-500" />
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="space-y-4">
          {cronLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>Aucun log d'exécution</p>
              <p className="text-sm">Les logs apparaîtront après la première exécution automatique</p>
            </div>
          ) : (
            cronLogs.map((log, idx) => (
              <motion.div
                key={log.id || idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-[#1a1a25] rounded-xl p-4 border border-cyan-500/20"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {log.status === 'success' ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    )}
                    <span className="text-white font-medium">
                      {new Date(log.executedAt).toLocaleString('fr-FR')}
                    </span>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${
                    log.status === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {log.status === 'success' ? 'Succès' : 'Partiel'}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="bg-[#0f0f15] rounded-lg p-2">
                    <p className="text-gray-500">Total envoyés</p>
                    <p className="text-cyan-400 font-bold">{log.results?.totalSent || 0}</p>
                  </div>
                  <div className="bg-[#0f0f15] rounded-lg p-2">
                    <p className="text-gray-500">Expirés</p>
                    <p className="text-orange-400 font-bold">{log.results?.expired || 0}</p>
                  </div>
                  <div className="bg-[#0f0f15] rounded-lg p-2">
                    <p className="text-gray-500">Erreurs</p>
                    <p className="text-red-400 font-bold">{log.results?.errorsCount || 0}</p>
                  </div>
                  <div className="bg-[#0f0f15] rounded-lg p-2">
                    <p className="text-gray-500">Durée</p>
                    <p className="text-white font-bold">{(log.duration || 0).toFixed(1)}s</p>
                  </div>
                </div>
                {log.results?.details && (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {Object.entries(log.results.details).map(([type, count]) => (
                      <span key={type} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                        {type}: {count}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Modal prévisualisation */}
      <AnimatePresence>
        {selectedEmail && (
          <EmailPreviewModal email={selectedEmail} onClose={() => setSelectedEmail(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default SpaceAutomation;
